import React, { useEffect, useState } from "react";
import {
  Plus, Edit2, Trash2, Eye, Users, Award, Globe, TrendingUp,
  ChevronRight, BarChart2, Mail, GripVertical,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [bookingCount, setBookingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/artists"),
        api.get("/bookings"),
      ]);
      setArtists(a.data);
      setBookingCount(b.data.length);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, []);

  const handleDelete = async (id) => {
    await api.delete(`/artists/${id}`);
    setDeleteConfirm(null);
    await load();
  };

  // ── Drag & drop reordering ─────────────────────────────────
  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    // Some browsers require data to actually start the drag
    try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };

  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };

  const onDragLeave = () => setOverId(null);

  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = draggingId;
    setDraggingId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;

    const next = [...artists];
    const fromIdx = next.findIndex((a) => a.id === sourceId);
    const toIdx = next.findIndex((a) => a.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);

    // Optimistic update
    setArtists(next);
    setSavingOrder(true);
    try {
      await api.patch("/artists/reorder", { ids: next.map((a) => a.id) });
    } catch (err) {
      // Revert on failure
      await load();
    } finally {
      setSavingOrder(false);
    }
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setOverId(null);
  };

  const totalAwards = artists.reduce((sum, a) => sum + parseInt(a.stats?.[3]?.value || "0") || 0, 0);
  const maxCountries = artists.length ? Math.max(...artists.map((a) => parseInt(a.stats?.[2]?.value || "0") || 0)) : 0;
  const totalProductions = artists.reduce((sum, a) => sum + (parseInt(a.stats?.[1]?.value || "0") || 0), 0);

  const stats = [
    { icon: <Users size={20} />, label: "Total Artists", value: artists.length },
    { icon: <Award size={20} />, label: "Award Wins", value: totalAwards },
    { icon: <Globe size={20} />, label: "Countries Reached", value: maxCountries + "+" },
    { icon: <TrendingUp size={20} />, label: "Productions", value: totalProductions },
  ];

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Staff Access</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <h1 className="font-display font-light text-5xl md:text-6xl text-white leading-tight">Admin Dashboard</h1>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/admin/bookings")}
              className="btn-outline-gold px-6 py-3.5 text-xs rounded-sm flex items-center gap-3 w-fit"
              data-testid="admin-view-bookings"
            >
              <Mail size={14} />
              <span>Bookings ({bookingCount})</span>
            </button>
            <button
              onClick={() => navigate("/admin/artists/new")}
              className="btn-gold px-7 py-3.5 text-xs rounded-sm flex items-center gap-3 w-fit"
              data-testid="admin-add-artist"
            >
              <Plus size={14} />
              <span>Add New Artist</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s) => (
            <div key={s.label} className="glass rounded-sm p-6 flex items-center gap-4">
              <div className="text-gold/50">{s.icon}</div>
              <div>
                <div className="stat-number text-3xl font-bold">{s.value}</div>
                <div className="font-body text-white/30 text-[0.6rem] tracking-wider uppercase">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Artist Table */}
        <div className="glass rounded-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gold/10 flex items-center justify-between">
            <h2 className="font-heading text-sm font-semibold tracking-widest text-white/60 uppercase">
              Artist Roster — {artists.length} Members
            </h2>
            <div className="flex items-center gap-3">
              {savingOrder && (
                <span
                  className="font-body text-gold/70 text-[0.55rem] tracking-[0.3em] uppercase flex items-center gap-2"
                  data-testid="admin-order-saving"
                >
                  <span className="w-3 h-3 border border-gold/30 border-t-gold rounded-full animate-spin" />
                  Saving order…
                </span>
              )}
              <span className="hidden md:inline font-body text-white/25 text-[0.55rem] tracking-[0.3em] uppercase">
                Drag rows to reorder
              </span>
              <BarChart2 size={16} className="text-gold/30" />
            </div>
          </div>

          <div className="divide-y divide-white/5">
            {loading ? (
              <div className="py-16 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
              </div>
            ) : artists.length === 0 ? (
              <div className="py-16 text-center">
                <Users size={40} className="text-white/10 mx-auto mb-4" />
                <p className="font-body text-white/20 text-sm">No artists yet. Add your first artist.</p>
              </div>
            ) : (
              artists.map((artist) => (
                <div
                  key={artist.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, artist.id)}
                  onDragOver={(e) => onDragOver(e, artist.id)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, artist.id)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors group select-none ${
                    draggingId === artist.id ? "opacity-40" : ""
                  } ${overId === artist.id && draggingId && draggingId !== artist.id ? "bg-gold/5 ring-1 ring-inset ring-gold/40" : ""}`}
                  data-testid={`admin-artist-row-${artist.id}`}
                >
                  <div
                    className="cursor-grab active:cursor-grabbing text-white/25 hover:text-gold transition-colors flex-shrink-0"
                    title="Drag to reorder"
                    data-testid={`admin-artist-drag-${artist.id}`}
                  >
                    <GripVertical size={16} />
                  </div>
                  <div className="relative w-12 h-12 rounded-sm overflow-hidden flex-shrink-0">
                    <img src={artist.image} alt={artist.name} className="w-full h-full object-cover object-top" draggable={false} />
                    <div className="absolute inset-0 border border-gold/20" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-lg text-white">{artist.name}</h3>
                      {artist.featured && <span className="specialty-tag" style={{ fontSize: "0.5rem" }}>Featured</span>}
                    </div>
                    <p className="font-body text-white/30 text-xs tracking-wide">{artist.title}</p>
                  </div>
                  <div className="hidden md:flex gap-1.5 flex-wrap">
                    {artist.specialties.slice(0, 2).map((s) => (
                      <span key={s} className="specialty-tag" style={{ fontSize: "0.55rem" }}>{s}</span>
                    ))}
                  </div>
                  <div className="hidden lg:flex items-center gap-6 text-right">
                    <div>
                      <div className="stat-number text-lg font-bold">{artist.stats?.[0]?.value}</div>
                      <div className="font-body text-white/25 text-[0.55rem] uppercase tracking-wider">Yrs</div>
                    </div>
                    <div>
                      <div className="stat-number text-lg font-bold">{artist.stats?.[1]?.value}</div>
                      <div className="font-body text-white/25 text-[0.55rem] uppercase tracking-wider">Prods</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/artists/${artist.id}`)}
                      className="w-8 h-8 flex items-center justify-center rounded-sm border border-white/10 hover:border-gold/40 hover:text-gold transition-all text-white/40"
                      title="View"
                      data-testid={`admin-view-${artist.id}`}
                    >
                      <Eye size={13} />
                    </button>
                    <button
                      onClick={() => navigate(`/admin/artists/${artist.id}/edit`)}
                      className="w-8 h-8 flex items-center justify-center rounded-sm border border-white/10 hover:border-gold/40 hover:text-gold transition-all text-white/40"
                      title="Edit"
                      data-testid={`admin-edit-${artist.id}`}
                    >
                      <Edit2 size={13} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(artist.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-sm border border-white/10 hover:border-red-500/50 hover:text-red-400 transition-all text-white/40"
                      title="Delete"
                      data-testid={`admin-delete-${artist.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 justify-between items-center">
          <button
            onClick={() => navigate("/admin/gallery")}
            className="flex items-center gap-2 text-white/30 hover:text-gold text-xs font-body tracking-widest uppercase transition-colors"
            data-testid="admin-go-gallery"
          >
            <span>Manage Gallery</span>
            <ChevronRight size={12} />
          </button>
          <button
            onClick={() => navigate("/admin/projects")}
            className="flex items-center gap-2 text-white/30 hover:text-gold text-xs font-body tracking-widest uppercase transition-colors"
            data-testid="admin-go-projects"
          >
            <span>Manage Projects</span>
            <ChevronRight size={12} />
          </button>
          <button
            onClick={() => navigate("/admin/users")}
            className="flex items-center gap-2 text-white/30 hover:text-gold text-xs font-body tracking-widest uppercase transition-colors"
          >
            <span>Manage Users</span>
            <ChevronRight size={12} />
          </button>
          <button
            onClick={() => navigate("/portal")}
            className="flex items-center gap-2 text-white/30 hover:text-gold text-xs font-body tracking-widest uppercase transition-colors"
          >
            <span>View Public Portal</span>
            <ChevronRight size={12} />
          </button>
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="delete-confirm-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
          <div className="relative glass-dark rounded-sm p-8 max-w-sm w-full">
            <h3 className="font-display text-2xl text-white mb-3">Confirm Delete</h3>
            <p className="font-body text-white/50 text-sm mb-6">
              Are you sure you want to remove{" "}
              <span className="text-gold">{artists.find((a) => a.id === deleteConfirm)?.name}</span>
              {" "}from the roster? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-3 text-xs rounded-sm font-body font-semibold tracking-widest uppercase bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-colors"
                data-testid="delete-confirm-yes"
              >
                Delete
              </button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 btn-outline-gold py-3 text-xs rounded-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
